import {
  SHARED_TRANSITION_EASING_EMPHASIZED,
  SHARED_TRANSITION_EASING_SPRING,
  SHARED_TRANSITION_MORPH_FADE_MS,
  SHARED_TRANSITION_MORPH_FLIGHT_MS,
  SHARED_TRANSITION_MORPH_INTENT_TTL_MS,
  SHARED_TRANSITION_MORPH_LANDING_TIMEOUT_MS,
  SHARED_TRANSITION_MORPH_LIFT_MS,
  SHARED_TRANSITION_WATCH_ROUTE_NAME,
} from '#shared/constants/transition'

interface MorphIntent {
  id: string
  source: HTMLImageElement
  createdAt: number
}

interface MorphFlight {
  id: string
  ghost: HTMLDivElement
  source: HTMLImageElement
  timeoutId: number
}

const MORPH_CLASS = 'is-poster-morph'

const px = (value: number) => `${value}px`

const nextFrame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

const settle = (animation: Animation, durationMs: number) => Promise.race([
  animation.finished.then(() => true, () => false),
  new Promise<boolean>(resolve => window.setTimeout(() => resolve(true), durationMs + 80)),
])

export default defineNuxtPlugin({
  name: 'app:poster-morph',

  setup(nuxtApp) {
    const router = useRouter()
    const root = document.documentElement
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    let intent: MorphIntent | null = null
    let flight: MorphFlight | null = null

    const readRadius = (element: Element) => getComputedStyle(element).borderRadius || '0px'

    const liftedShadow = () => `0 24px 60px -12px ${getComputedStyle(root).getPropertyValue('--theme-shadow-4').trim()}`

    const restingShadow = () => `0 12px 40px -8px ${getComputedStyle(root).getPropertyValue('--theme-shadow-3').trim()}`

    const createGhost = (source: HTMLImageElement) => {
      const rect = source.getBoundingClientRect()
      const ghost = document.createElement('div')
      ghost.className = 'app-poster-morph'
      ghost.setAttribute('aria-hidden', 'true')
      Object.assign(ghost.style, {
        left: px(rect.left),
        top: px(rect.top),
        width: px(rect.width),
        height: px(rect.height),
        borderRadius: readRadius(source.closest('.ui-watch-card-base__header, .ui-watch-card-recommendation') ?? source),
      })

      const image = document.createElement('img')
      image.className = 'app-poster-morph__image'
      image.src = source.currentSrc || source.src
      image.alt = ''
      ghost.append(image)
      document.body.append(ghost)
      return ghost
    }

    const finish = (current: MorphFlight) => {
      window.clearTimeout(current.timeoutId)
      current.ghost.remove()
      current.source.classList.remove('is-poster-morph-source')
      root.classList.remove(MORPH_CLASS)
      document.querySelectorAll('.is-poster-morph-landed').forEach(element => element.classList.remove('is-poster-morph-landed'))
      if (flight === current) {
        flight = null
      }
    }

    const abort = (current: MorphFlight | null) => {
      if (!current) return
      const fade = current.ghost.animate(
        [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.92)' }],
        { duration: SHARED_TRANSITION_MORPH_FADE_MS, easing: 'ease-in', fill: 'forwards' },
      )
      void settle(fade, SHARED_TRANSITION_MORPH_FADE_MS).then(() => finish(current))
    }

    const launch = (current: MorphIntent) => {
      abort(flight)

      const ghost = createGhost(current.source)
      current.source.classList.add('is-poster-morph-source')
      root.classList.add(MORPH_CLASS)

      ghost.animate(
        [
          { transform: 'scale(1)', boxShadow: '0 0 0 0 transparent' },
          { transform: 'scale(1.04)', boxShadow: liftedShadow() },
        ],
        { duration: SHARED_TRANSITION_MORPH_LIFT_MS, easing: SHARED_TRANSITION_EASING_SPRING, fill: 'forwards' },
      )

      const started: MorphFlight = { id: current.id, ghost, source: current.source, timeoutId: 0 }
      started.timeoutId = window.setTimeout(() => abort(started), SHARED_TRANSITION_MORPH_LANDING_TIMEOUT_MS)
      flight = started
    }

    const land = async (current: MorphFlight) => {
      await nextFrame()
      await nextFrame()

      if (flight !== current) return

      const target = document.querySelector<HTMLElement>(`[data-poster-morph-target="${CSS.escape(current.id)}"]`)
      if (!target) {
        abort(current)
        return
      }

      const from = current.ghost.getBoundingClientRect()
      const to = target.getBoundingClientRect()
      const radius = readRadius(target.closest('.ui-watch-player') ?? target)

      current.ghost.getAnimations().forEach(animation => animation.cancel())

      const flightAnimation = current.ghost.animate(
        [
          { left: px(from.left), top: px(from.top), width: px(from.width), height: px(from.height), borderRadius: current.ghost.style.borderRadius, boxShadow: liftedShadow() },
          { left: px(to.left), top: px(to.top), width: px(to.width), height: px(to.height), borderRadius: radius, boxShadow: restingShadow() },
        ],
        { duration: SHARED_TRANSITION_MORPH_FLIGHT_MS, easing: SHARED_TRANSITION_EASING_EMPHASIZED, fill: 'forwards' },
      )

      const landed = await settle(flightAnimation, SHARED_TRANSITION_MORPH_FLIGHT_MS)

      if (!landed || flight !== current) return

      target.classList.add('is-poster-morph-landed')
      const fade = current.ghost.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: SHARED_TRANSITION_MORPH_FADE_MS, easing: 'ease-out', fill: 'forwards' },
      )
      void settle(fade, SHARED_TRANSITION_MORPH_FADE_MS).then(() => finish(current))
    }

    document.addEventListener('click', (event) => {
      if (reducedMotion.matches || document.visibilityState !== 'visible' || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        intent = null
        return
      }

      const card = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-poster-morph-id]') : null
      const source = card?.querySelector<HTMLImageElement>('img[data-poster-morph-source]')
      const id = card?.dataset.posterMorphId

      intent = card && source && id && source.naturalWidth ? { id, source, createdAt: performance.now() } : null
    }, { capture: true })

    router.beforeEach((to) => {
      const current = intent
      intent = null

      if (!current || to.name !== SHARED_TRANSITION_WATCH_ROUTE_NAME) return
      if (String(to.params.kinopoiskId) !== current.id) return
      if (performance.now() - current.createdAt > SHARED_TRANSITION_MORPH_INTENT_TTL_MS) return
      if (!current.source.isConnected) return

      launch(current)
    })

    router.afterEach((to, _from, failure) => {
      if (!flight) return
      if (failure || to.name !== SHARED_TRANSITION_WATCH_ROUTE_NAME || String(to.params.kinopoiskId) !== flight.id) {
        abort(flight)
      }
    })

    router.onError(() => abort(flight))

    nuxtApp.hook('page:transition:finish', () => {
      if (flight) {
        void land(flight)
      }
    })

    nuxtApp.hook('app:error', () => abort(flight))
  },
})
